import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Button,
    Container,
    Divider,
    Flex,
    Group,
    Loader,
    Notification,
    Paper,
    Radio,
    Select,
    Stack,
    Text,
    TextInput,
    Title,
    Switch,
} from '@mantine/core';
import { CalendarDateInput } from '../../global/component/molecules/CalendarDateInput';
import { inject, observer } from 'mobx-react';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { useForm, zodResolver } from '@mantine/form';
import * as z from 'zod';
import i18n, { t } from 'i18next';
import { Trans } from 'react-i18next';
import { IconInfoCircle, IconPhone } from '@tabler/icons-react';
import Utils from '../../global/helpers/utils.ts';
import { useNavigate } from 'react-router-dom';
import { useGetPatientsProfile } from '../../global/entities/Patients/queries/useGetPatientsProfiles.tsx';
import { theme } from '../../global/style/theme.tsx';
import { useGetPatientInvitationToken } from '../../global/entities/Patients/queries/useGetPatientInvitationToken.tsx';
import { usePutPatientsProfile } from '../../global/entities/Patients/mutations/usePutPatientsMutations.tsx';
import { useSessionProvider } from '../../global/provider/sessionContext.tsx';
import { AppMedError } from '../../global/error/AppMedError.ts';
import { usePutPatientsInitialProfile } from '../../global/entities/Patients/mutations/usePutPatientsInitialProfileMutations.tsx';
import { IPatientsInitialProfilePayload, IPatientsProfilePayload } from '../../global/entities/Patients/Patient.ts';

const formatPostalCode = (value: string): string => {
    const raw = value.replace(/\s/g, '').toUpperCase();
    if (raw.length > 3)
        return raw.slice(0, 3) + ' ' + raw.slice(3, 6);

    return raw;
};

// Fields the onboarding (initial profile) form can collect. If a clinic hides all of
// them, the onboarding screen has nothing to answer and is auto-submitted (AMI-612).
const ONBOARDING_FIELD_KEYS = ['birthDate', 'sexID', 'postalCode', 'phoneNumber', 'contactableForResearch'];

const PatientProfileForm: React.FC = inject()(
    observer(() => {
        const isSmallScreen = useMediaQuery('(max-width: 576px)');
        const [error, setError] = useState<string | null>(null);
        const [isSubmitting, setIsSubmitting] = useState(false);
        const usePutPatientsInitialProfileMutation = usePutPatientsInitialProfile();
        const usePutPatientsProfileMutation = usePutPatientsProfile();
        const { data: patientProfile, refetch: refetchProfile } = useGetPatientsProfile();
        const { data: token } = useGetPatientInvitationToken();
        const [opened, { close: closeAlert }] = useDisclosure(true);
        const navigate = useNavigate();
        const { currentUser } = useSessionProvider();

        const isFieldHidden = useCallback(
            (fieldKey: string): boolean =>
                patientProfile?.hiddenRegistrationFields?.includes(fieldKey) ?? false,
            [patientProfile],
        );

        const isOnboarding = !currentUser?.hasProfile;
        const allOnboardingFieldsHidden = useMemo(
            () => !!patientProfile && ONBOARDING_FIELD_KEYS.every((key) => isFieldHidden(key)),
            [patientProfile, isFieldHidden],
        );

        const schema = useMemo(
            () =>
                z.object({
                    firstName: z.preprocess((val) => val ?? '', z.string().nonempty(t('formError.firstName'))),
                    lastName: z.preprocess((val) => val ?? '', z.string().nonempty(t('formError.lastName'))),
                    birthDate: isFieldHidden('birthDate')
                        ? z.any()
                        : z.preprocess(
                              (val) => (val ? val : new Date('invalid')), // Force un objet Date même si null
                              z
                                  .date()
                                  .refine((date) => !isNaN(date.getTime()), {
                                      message: t('formError.dateOfBirth'),
                                  })
                                  .refine(
                                      (date) => {
                                          const today = new Date();
                                          const minAge = 0;
                                          const maxAge = 120;
                                          const age =
                                              today.getFullYear() -
                                              date.getFullYear() -
                                              (today < new Date(today.getFullYear(), date.getMonth(), date.getDate()) ? 1 : 0);
                                          return age >= minAge && age <= maxAge;
                                      },
                                      { message: t('formError.birthDateOutOfRange') },
                                  ),
                          ),

                    sexID: isFieldHidden('sexID')
                        ? z.any()
                        : z.number().min(1, { message: t('formError.sexID') }),
                    postalCode: isFieldHidden('postalCode')
                        ? z.any()
                        : z.preprocess(
                              (val) => val ?? '',
                              z
                                  .string()
                                  .nonempty(t('formError.postalCode'))
                                  .regex(
                                      /^[0-9]{5}(?:-[0-9]{4})?$|^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/,
                                      t('formError.postalCodeInvalid'),
                                  ),
                          ),
                    contactableForResearch: isFieldHidden('contactableForResearch')
                        ? z.any()
                        : z.boolean({
                              required_error: t('formError.consent'),
                              invalid_type_error: t('formError.consent'),
                          }),
                }),
            [isFieldHidden],
        );

        const form = useForm<PatientFormValues>({
            mode: 'controlled',
            initialValues: {
                firstName: '',
                lastName: '',
                birthDate: undefined,
                sexID: 0,
                postalCode: '',
                phoneNumber: '',
                allowSMS: false,
                contactableForResearch: null,
            },
            // Use a function that always references the current schema
            validate: (values) => {
                const resolver = zodResolver(schema);
                return resolver(values);
            },
        });

        useEffect(() => {
            if (patientProfile) {
                form.reset();
                form.setValues(patientProfile as PatientFormValues);
            }
        }, [patientProfile]);

        const handleSubmitCreate = async (values: typeof form.values) => {
            setError(null);
            setIsSubmitting(true);
            try {
                const payload: IPatientsInitialProfilePayload = {
                    sexID: values.sexID,
                    birthDate: values.birthDate!,
                    postalCode: values.postalCode,
                    phoneNumber: values.phoneNumber || undefined,
                    allowSMS: values.allowSMS,
                    language: i18n.language,
                    contactableForResearch: values.contactableForResearch ?? false,
                };
                await usePutPatientsInitialProfileMutation.mutateAsync(payload);
            } catch (e) {
                if (e instanceof AppMedError)
                    setError(e.message);
                 else
                    setError('An unexpected error occurred.');

            } finally {
                setIsSubmitting(false);
                refetchProfile();
                navigate(`/patientDashboard/initialProfile/${token}`);
            }
        };

        // When the clinic hides every onboarding field there is nothing for the patient to
        // answer, so bypass the screen by auto-submitting (backend fills the configured defaults).
        const autoSubmittedRef = useRef(false);
        useEffect(() => {
            if (!patientProfile || !isOnboarding || !allOnboardingFieldsHidden) return;
            if (autoSubmittedRef.current) return;
            autoSubmittedRef.current = true;
            handleSubmitCreate(form.values);
        }, [patientProfile, isOnboarding, allOnboardingFieldsHidden]);

        const handleSubmitUpdate = async (values: typeof form.values) => {
            setError(null);
            setIsSubmitting(true);
            try {
                const payload: IPatientsProfilePayload = {
                    firstName: values.firstName,
                    lastName: values.lastName,
                    sexID: values.sexID,
                    birthDate: values.birthDate!,
                    postalCode: values.postalCode,
                    phoneNumber: values.phoneNumber || undefined,
                    allowSMS: values.allowSMS,
                    language: i18n.language,
                    contactableForResearch: values.contactableForResearch ?? false,
                };
                await usePutPatientsProfileMutation.mutateAsync(payload);
            } catch (e) {
                if (e instanceof AppMedError)
                    setError(e.message);
                 else
                    setError('An unexpected error occurred.');

            } finally {
                setIsSubmitting(false);
                refetchProfile();
                navigate('/patientDashboard/mainPatient');
            }
        };

        if (isOnboarding && allOnboardingFieldsHidden) {
            return (
                <Container size={'xs'}>
                    <Stack align="center" mt={'xl'} gap={'md'}>
                        <Loader />
                        <Text>{t('formProfile.completingProfile')}</Text>
                    </Stack>
                </Container>
            );
        }

        return (
            <Container size={'xs'}>
                <Title mb={'md'} order={4} mt={'xl'}>
                    {t('formProfile.profileSetting')}
                </Title>

                <Alert
                    hidden={!opened}
                    onClose={closeAlert}
                    withCloseButton
                    icon={<IconInfoCircle />}
                    mt={'md'}
                    mb={'md'}
                    variant={'light'}
                    title={t('formProfile.alert.title')}
                >
                    {t('formProfile.alert.text')}
                </Alert>
                {currentUser?.hasProfile && (patientProfile?.primaryHcpName || patientProfile?.therapeuticIndicationName) && (
                    <Paper p="md" mb="md" withBorder>
                        <Stack gap="xs">
                            {patientProfile?.primaryHcpName && (
                                <Group gap="xs">
                                    <Text size="sm" fw={600}>{t('formProfile.primaryPhysician')}:</Text>
                                    <Text size="sm">{patientProfile.primaryHcpName}</Text>
                                </Group>
                            )}
                            {patientProfile?.therapeuticIndicationName && (
                                <Group gap="xs">
                                    <Text size="sm" fw={600}>{t('formProfile.therapeuticArea')}:</Text>
                                    <Text size="sm">{patientProfile.therapeuticIndicationName}</Text>
                                </Group>
                            )}
                        </Stack>
                    </Paper>
                )}
                <Paper style={{ borderTopLeftRadius: opened ? theme.radius?.lg : 0 }}>
                    <form
                        onSubmit={form.onSubmit((values) => {
                            if (currentUser?.hasProfile)
                                handleSubmitUpdate(values);
                             else
                                handleSubmitCreate(values);

                        })}
                    >
                        <Stack gap={'md'}>
                            <Title order={4} c={'blue'}>
                                {t('formProfile.personalInformation')}
                            </Title>
                            {currentUser?.hasProfile && (
                                <>
                                    <TextInput
                                        label={t('formProfile.firstName')}
                                        placeholder={t('formProfile.firstName')}
                                        {...form.getInputProps('firstName')}
                                    />
                                    <TextInput
                                        label={t('formProfile.lastName')}
                                        placeholder={t('formProfile.lastName')}
                                        {...form.getInputProps('lastName')}
                                    />
                                </>
                            )}
                            {!isFieldHidden('birthDate') && (
                                <CalendarDateInput
                                    label={t('formProfile.birthDate')}
                                    placeholder={t('formProfile.birthDatePlaceholder')}
                                    description={t('formProfile.birthDateDescription')}
                                    {...form.getInputProps('birthDate')}
                                />
                            )}
                            {!isFieldHidden('sexID') && (
                                <Select
                                    label={t('formProfile.sexIDTitle')}
                                    placeholder={t('formProfile.sexIDPlaceholder')}
                                    data={patientProfile?.sexesCombobox}
                                    onChange={(value) => form.setFieldValue('sexID', Number(value))}
                                    value={form.values.sexID > 0 ? form.values.sexID.toString() : null}
                                    error={form.getInputProps('sexID').error}
                                />
                            )}
                            {!isFieldHidden('postalCode') && (
                                <TextInput
                                    label={t('formProfile.postalCode')}
                                    maxLength={7}
                                    placeholder={t('formProfile.postalCodePlaceholder')}
                                    {...form.getInputProps('postalCode')}
                                    onChange={(e) => {
                                        const formatted = formatPostalCode(e.currentTarget.value);
                                        form.setFieldValue('postalCode', formatted);
                                    }}
                                />
                            )}
                            {!isFieldHidden('phoneNumber') && (
                                <>
                                    <Divider my="sm" />
                                    <Title order={4} c={'blue'}>
                                        {t('formProfile.smsNotifications')}
                                    </Title>
                                    <Text size="sm" c="dimmed">
                                        {t('formProfile.allowSmsDescription')}
                                    </Text>
                                    <TextInput
                                        label={t('formProfile.phoneNumber')}
                                        placeholder="(415) 555-1212"
                                        leftSection={<IconPhone size={16} />}
                                        {...form.getInputProps('phoneNumber')}
                                        onBlur={(e) => form.setFieldValue('phoneNumber', Utils.formatPhoneNumber(e.currentTarget.value))}
                                    />
                                    {patientProfile?.smsOptOutSource === 1 ? (
                                        <Alert variant="light" color="orange" icon={<IconInfoCircle />}>
                                            <Trans
                                                i18nKey="formProfile.smsOptOutByStopWord"
                                                values={{ phone: patientProfile.smsPhoneNumber }}
                                                components={{
                                                    1: <a href={`sms:${patientProfile.smsPhoneNumber}?body=START`} style={{ fontWeight: 600 }} />,
                                                    2: <strong />,
                                                }}
                                            />
                                        </Alert>
                                    ) : (
                                        <Switch
                                            label={t('formProfile.allowSmsNotifications')}
                                            disabled={!form.values.phoneNumber}
                                            checked={form.values.allowSMS}
                                            onChange={(event) => form.setFieldValue('allowSMS', event.currentTarget.checked)}
                                        />
                                    )}
                                </>
                            )}
                            {!isFieldHidden('contactableForResearch') && (
                                <>
                                    <Divider my="sm" />
                                    <Title order={4} c={'blue'}>
                                        {t('formProfile.contactableForResearchTitle')}
                                    </Title>
                                    <Radio.Group
                                        label={t('formProfile.contactableForResearch')}
                                        value={
                                            form.values.contactableForResearch
                                                ? 'yes'
                                                : form.values.contactableForResearch == false
                                                  ? 'no'
                                                  : undefined
                                        }
                                        onChange={(val: string) => {
                                            const Value: boolean = val === 'yes';
                                            form.setFieldValue('contactableForResearch', Value);
                                        }}
                                        error={form.errors.contactableForResearch}
                                    >
                                        <Flex gap="md" mt="sm">
                                            <Radio value="yes" label={t('formProfile.yes')} size="sm" styles={{ label: { fontSize: 16 } }} />
                                            <Radio value="no" label={t('formProfile.no')} size="sm" styles={{ label: { fontSize: 16 } }} />
                                        </Flex>
                                    </Radio.Group>
                                </>
                            )}
                            <Divider my="sm" />
                            <Flex direction={isSmallScreen ? 'column' : 'row'} gap={'sm'} mt={'sm'}>
                                <Button
                                    onClick={() => {
                                        navigate(-1);
                                    }}
                                    w={isSmallScreen ? '100%' : '50%'}
                                    variant={'default'}
                                    type="button"
                                    color="gray"
                                    loading={isSubmitting}
                                >
                                    {t('formProfile.cancel')}
                                </Button>
                                <Button w={isSmallScreen ? '100%' : '50%'} type="submit" ml={'auto'}>
                                    {t('formProfile.save')}
                                </Button>
                            </Flex>
                        </Stack>
                    </form>
                </Paper>
                {error && (
                    <Notification color="red" onClose={() => setError(null)}>
                        {error}
                    </Notification>
                )}
            </Container>
        );
    })
);

type PatientFormValues = {
    firstName: string;
    lastName: string;
    birthDate: Date | undefined;
    sexID: number;
    postalCode: string;
    phoneNumber: string;
    allowSMS: boolean;
    contactableForResearch: boolean | null;
};

export default PatientProfileForm;
